import { ChangeEvent, ReactNode, RefCallback, useEffect, useRef, forwardRef } from 'react'
import classNames from 'classnames/bind'

import { mergeRefs } from 'common/lib/mergeRefs'

import styles from './plain-checkbox.module.scss'
import { dispatchFormChanged } from '../custom-events'

const cx = classNames.bind(styles)

export type Props = {
  name?: string
  value: string
  label?: string | ReactNode
  hideLabel?: boolean // скрыт, но доступен для скрин-ридеров
  skipLabel?: boolean
  skipLeftShift?: boolean
  checked?: boolean | null
  defaultChecked?: boolean
  id?: string
  disabled?: boolean
  className?: string
  labelClassName?: string
  indeterminate?: boolean
  reference?: RefCallback<HTMLInputElement>
  level?: number
  style?: object
  form?: string
  large?: boolean

  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onFocus?: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void
}

/**
 * Checkbox
 * Цвет по умолчанию $primary-color, но можно задать через css переменную в className --check-color
 */

const PlainCheckbox = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      value,
      label,
      hideLabel,
      skipLabel,
      skipLeftShift,
      checked,
      defaultChecked,
      id,
      disabled,
      className,
      labelClassName,
      indeterminate,
      reference,
      level,
      style,
      form,
      large,

      onChange,
      onFocus,
      onBlur,
    },
    forwardedRef,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null)

    const currentId = id || `${name || ''}_${value}`

    const levelClassName = { [`level${level || ''}`]: level }
    const rootClassName = cx(
      styles.root,
      { disabled, hideLabel, large, ...levelClassName },
      className,
    )
    const checkboxClassName = cx(styles.check, {
      disabled: disabled || indeterminate,
      skipLabel,
      hideLabel,
      leftShift: !skipLeftShift,
    })
    const labelClassNameFull = cx(styles.label, { hidden: hideLabel, disabled }, labelClassName)

    const containerClassName = skipLabel
      ? cx(styles.container, { skipLabel, ...levelClassName }, className)
      : cx(styles.container, { hideLabel })

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = !!indeterminate
      }
    }, [indeterminate])

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      dispatchFormChanged()
    }

    const renderControl = (
      <div className={containerClassName}>
        <input
          id={currentId}
          type="checkbox"
          name={name}
          value={value}
          checked={typeof checked === 'boolean' ? checked : undefined}
          defaultChecked={defaultChecked}
          disabled={disabled}
          className={styles.input}
          ref={mergeRefs(inputRef, forwardedRef, reference)}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          form={form}
        />

        <span className={checkboxClassName} />

        {label && !skipLabel && <span className={labelClassNameFull}>{label}</span>}
      </div>
    )

    if (skipLabel) {
      return renderControl
    }

    return (
      <label className={rootClassName} style={style}>
        {renderControl}
      </label>
    )
  },
)

PlainCheckbox.displayName = 'PlainCheckbox'

export default PlainCheckbox
